import React, { useState, useEffect } from 'react';
import {
  Typography, Grid, Paper, CircularProgress, Box, Button,
  Card, CardContent, CardActions, Avatar, Chip, Divider,
  List, ListItem, ListItemText, ListItemIcon, Fade, Alert,
  Snackbar, useTheme, alpha, Skeleton, Tooltip, IconButton,
} from '@mui/material';
import {
  School as SchoolIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  BarChart as BarChartIcon,
  Security as SecurityIcon,
  DateRange as DateRangeIcon,
  Class as ClassIcon,
  Subject as SubjectIcon,
  CreditCard as CreditCardIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import GraficoCampanhas from '../../components/Dashboard/GraficoCampanhas';
import { getAdminDashboardData } from '../../services/dashboardService';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import AdminUserCreation from './AdminUserCreation';
import PropTypes from 'prop-types';
import api from '../../services/api';

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, subtitle, icon, accentColor, loading }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(accentColor, 0.2)}`,
        borderLeft: `4px solid ${accentColor}`,
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': { boxShadow: theme.shadows[4], transform: 'translateY(-2px)' },
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500} gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={60} height={40} />
            ) : (
              <Typography variant="h4" fontWeight={700} color="text.primary">
                {value ?? '—'}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {subtitle}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 1.2,
              borderRadius: 2,
              bgcolor: alpha(accentColor, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 28, color: accentColor } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subtitle: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  accentColor: PropTypes.string.isRequired,
  loading: PropTypes.bool,
};

// ── Function Card ─────────────────────────────────────────────────────────────
const FunctionCard = ({ func }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
          boxShadow: theme.shadows[3],
          borderColor: alpha(theme.palette.primary.main, 0.4),
        },
      }}
    >
      <CardContent sx={{ p: 2.5, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: 1.5 }}>
            {func.icon}
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {func.title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {func.description}
        </Typography>
      </CardContent>
      <CardActions sx={{ px: 2.5, pb: 2 }}>
        <Button
          component={Link}
          to={func.path}
          size="small"
          endIcon={<ArrowForwardIcon fontSize="small" />}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          Abrir
        </Button>
      </CardActions>
    </Card>
  );
};

FunctionCard.propTypes = { func: PropTypes.object.isRequired };

// ── Pending Institutions ──────────────────────────────────────────────────────
const InstituicoesPendentes = () => {
  const [pendentes, setPendentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    api.get('/instituicoes/pendentes/sem-plano')
      .then(({ data }) => setPendentes(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || pendentes.length === 0) return null;

  return (
    <Alert
      severity="warning"
      icon={<WarningIcon />}
      sx={{ borderRadius: 3, mb: 4, alignItems: 'flex-start' }}
    >
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {pendentes.length} instituiç{pendentes.length === 1 ? 'ão' : 'ões'} sem plano atribuído
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {pendentes.map((inst) => (
          <Chip
            key={inst.id}
            label={inst.nome}
            size="small"
            variant="outlined"
            color="warning"
            onClick={() => navigate(`/instituicoes/${inst.id}`)}
            onDelete={() => navigate(`/instituicoes/${inst.id}`)}
            deleteIcon={<OpenInNewIcon fontSize="small" />}
          />
        ))}
      </Box>
    </Alert>
  );
};

// ── Section Header ────────────────────────────────────────────────────────────
const SectionHeader = ({ title, subtitle }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="h6" fontWeight={700}>{title}</Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
    )}
  </Box>
);

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};

// ── Loading Skeleton ──────────────────────────────────────────────────────────
const LoadingSkeleton = () => (
  <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
    <Skeleton width={200} height={32} sx={{ mb: 1 }} />
    <Skeleton width={300} height={20} sx={{ mb: 4 }} />
    <Grid container spacing={2.5} sx={{ mb: 4 }}>
      {[1, 2, 3, 4, 5, 6].map(i => (
        <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
          <Skeleton variant="rectangular" height={110} sx={{ borderRadius: 3 }} />
        </Grid>
      ))}
    </Grid>
    <Grid container spacing={2.5}>
      {[1, 2, 3, 4, 5, 6].map(i => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

// ── Main Component ────────────────────────────────────────────────────────────
const AdminDashboardPage = () => {
  const theme = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      const data = await getAdminDashboardData();
      setDashboardData(data);
      setError(null);
    } catch {
      setError('Falha ao carregar os dados do dashboard.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const adminFunctions = [
    {
      title: 'Instituições',
      description: 'Gerir instituições, perfis e configurações.',
      icon: <SchoolIcon color="primary" fontSize="small" />,
      path: '/instituicoes',
    },
    {
      title: 'Campanhas',
      description: 'Criar e acompanhar campanhas de pré-inscrição.',
      icon: <CampaignIcon color="secondary" fontSize="small" />,
      path: '/campanhas',
    },
    {
      title: 'Estudantes',
      description: 'Consultar e gerir registos de estudantes.',
      icon: <PeopleIcon color="success" fontSize="small" />,
      path: '/estudantes',
    },
    {
      title: 'Cursos',
      description: 'Definir cursos disponíveis nas instituições.',
      icon: <SubjectIcon color="info" fontSize="small" />,
      path: '/cursos',
    },
    {
      title: 'Períodos',
      description: 'Configurar anos e períodos lectivos.',
      icon: <DateRangeIcon color="warning" fontSize="small" />,
      path: '/periodos',
    },
    {
      title: 'Classes',
      description: 'Gerir classes e turmas das instituições.',
      icon: <ClassIcon color="error" fontSize="small" />,
      path: '/classes',
    },
    {
      title: 'Disciplinas',
      description: 'Administrar o catálogo de disciplinas.',
      icon: <SubjectIcon color="primary" fontSize="small" />,
      path: '/disciplinas',
    },
    {
      title: 'Pré-Inscrições',
      description: 'Ver e processar pré-inscrições recebidas.',
      icon: <AssignmentIcon color="secondary" fontSize="small" />,
      path: '/pre-inscricoes',
    },
    {
      title: 'Relatórios',
      description: 'Gerar relatórios de actividade e estatísticas.',
      icon: <BarChartIcon color="success" fontSize="small" />,
      path: '/relatorios',
    },
    ...(user?.tipo === 'admin' ? [
      {
        title: 'Logs do Sistema',
        description: 'Auditoria de acções e eventos do sistema.',
        icon: <SecurityIcon color="warning" fontSize="small" />,
        path: '/admin/logs',
      },
    ] : []),
  ];

  if (loading) return <LoadingSkeleton />;

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          {error}
          <Button size="small" onClick={() => fetchData()} sx={{ ml: 2 }}>
            Tentar novamente
          </Button>
        </Alert>
      </Box>
    );
  }

  const stats = [
    {
      title: 'Instituições',
      value: dashboardData?.totalInstituicoes,
      subtitle: 'registadas no sistema',
      icon: <SchoolIcon />,
      accentColor: theme.palette.primary.main,
    },
    {
      title: 'Campanhas',
      value: dashboardData?.totalCampanhas,
      subtitle: 'campanhas activas',
      icon: <CampaignIcon />,
      accentColor: theme.palette.secondary.main,
    },
    {
      title: 'Estudantes',
      value: dashboardData?.totalEstudantes,
      subtitle: 'estudantes registados',
      icon: <PeopleIcon />,
      accentColor: theme.palette.success.main,
    },
    {
      title: 'Pré-Inscrições',
      value: dashboardData?.totalPreInscricoes,
      subtitle: 'pré-inscrições recebidas',
      icon: <AssignmentIcon />,
      accentColor: theme.palette.warning.main,
    },
  ];

  return (
    <Box sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04), minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>

        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Bom dia, {user?.nome?.split(' ')[0]} 👋
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visão geral do sistema Zenkroll — {new Date().toLocaleDateString('pt-MZ', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>
          <Tooltip title="Actualizar dados">
            <IconButton
              onClick={() => fetchData(true)}
              disabled={refreshing}
              sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.8)}`, borderRadius: 2 }}
            >
              {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Pending institutions alert — top priority */}
        <InstituicoesPendentes />

        {/* Stats */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader title="Resumo" subtitle="Indicadores principais do sistema" />
          <Grid container spacing={2.5}>
            {stats.map((s) => (
              <Grid item xs={12} sm={6} md={3} key={s.title}>
                <StatCard {...s} loading={!dashboardData} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Admin functions */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader
            title="Gestão do Sistema"
            subtitle="Acesso rápido às funcionalidades administrativas"
          />
          <Grid container spacing={2.5}>
            {adminFunctions.map((func) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={func.title}>
                <FunctionCard func={func} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Charts */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader title="Análise" subtitle="Dados de actividade e campanhas recentes" />
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  height: '100%',
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Pré-Inscrições por Instituição
                </Typography>
                <GraficoCampanhas data={dashboardData?.preInscricoesPorInstituicao} />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  height: '100%',
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Campanhas Recentes
                </Typography>
                {Array.isArray(dashboardData?.campanhasRecentes) && dashboardData.campanhasRecentes.length > 0 ? (
                  <List dense disablePadding>
                    {dashboardData.campanhasRecentes.map((campanha, i) => (
                      <React.Fragment key={campanha.id}>
                        <ListItem disablePadding sx={{ py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary={campanha.nome}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                          />
                          <Chip
                            label={`${campanha.preInscricoes?.length || 0}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </ListItem>
                        {i < dashboardData.campanhasRecentes.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: 'text.secondary' }}>
                    <HourglassIcon fontSize="small" />
                    <Typography variant="body2">Nenhuma campanha recente.</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Admin user creation — bottom, less prominent */}
        <Box sx={{ mb: 2 }}>
          <SectionHeader
            title="Gestão de Utilizadores Administrativos"
            subtitle="Criar e gerir contas de administradores do sistema"
          />
          <AdminUserCreation />
        </Box>

      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={toast.severity} sx={{ borderRadius: 2 }} onClose={() => setToast(t => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboardPage;
